'use client';

import type {Money} from '@dintero/money';

import {Button} from '@/modules/foundation/components/button';
import type {HeadingProps} from '@/modules/foundation/components/typography/heading';
import {Heading} from '@/modules/foundation/components/typography/heading';
import type {ParagraphProps} from '@/modules/foundation/components/typography/paragraph';
import {Paragraph} from '@/modules/foundation/components/typography/paragraph';

import type {CartState} from '../cart/hooks/useCart';
import {FormattedItemPrice} from '../item/FormattedItemPrice';
import type {ItemForPriceFragment} from '../item/query';

export type ProductFormPriceHeadingProps = HeadingProps & {
	priceIncVat: Money;
};

export type ProductFormButtonProps = React.ComponentProps<'button'> & {
	cart: CartState;
	cartLineId: string | null;
	isValid: boolean;
	shoppingDisabled?: boolean;
};

export type ProductFormCartLineIdFieldProps = React.ComponentProps<'input'> & {
	cartLineId: string | null;
};

export type ProductFormProps = React.ComponentProps<'form'> & {
	cart: CartState;
	cartLineId: string | null;
	isValid: boolean;
	shoppingDisabled?: boolean;
	selectedItems: ItemForPriceFragment[];
	priceIncVat: Money;
	buttonProps?: React.ComponentProps<'button'>;
	paragraphProps?: ParagraphProps;
	errorParagraphProps?: ParagraphProps;
};

export function ProductFormField({className = 'mb-8', ...rest}: React.ComponentProps<'div'>) {
	return <div className={className} {...rest} />;
}

export function ProductFormHeading({level = 3, className = 'mb-3', ...rest}: HeadingProps) {
	return <Heading level={level} className={className} {...rest} />;
}

export function ProductFormCartLineIdField({
	cartLineId,
	className = '',
	...rest
}: ProductFormCartLineIdFieldProps & React.InputHTMLAttributes<HTMLInputElement>) {
	if (!cartLineId || cartLineId.length === 0) {
		return null;
	}

	const mergedClassName = `${className} sr-only`;

	return (
		<input name="cartLineId" defaultValue={cartLineId} className={mergedClassName} {...rest} />
	);
}

export function ProductFormButton({
	cart,
	cartLineId,
	isValid,
	shoppingDisabled,
	...props
}: ProductFormButtonProps) {
	const isBusy = cart.isAddingToCart || cart.isRefreshing || cart.isUpdatingCart;

	const text = isBusy
		? cartLineId
			? 'Oppdaterer produkt i handlevogn'
			: 'Legger til i handlekurv...'
		: cartLineId
			? 'Oppdater produkt i handlevogn'
			: 'Legg i handlekurv';

	return (
		<ProductFormField>
			<Button type="submit" disabled={shoppingDisabled || !isValid || isBusy} {...props}>
				{text}
			</Button>
			{shoppingDisabled && (
				<Paragraph className="mt-4">
					Netthandel er midlertidig deaktivert. Sjekk tilbake senere.
				</Paragraph>
			)}
		</ProductFormField>
	);
}

export function ProductForm({
	cart,
	isValid,
	shoppingDisabled,
	selectedItems,
	cartLineId,
	buttonProps,
	paragraphProps,
	errorParagraphProps = {
		className: 'mt-4 text-red-600',
	},
	children,
	...formProps
}: ProductFormProps) {
	return (
		<form {...formProps}>
			{children}
			<div className="text-primary font-display mb-8 text-4xl font-semibold">
				<h2 className="sr-only">Pris</h2>
				<FormattedItemPrice items={selectedItems} />
			</div>{' '}
			<ProductFormCartLineIdField cartLineId={cartLineId} />
			<ProductFormButton
				cart={cart}
				cartLineId={cartLineId}
				isValid={isValid}
				shoppingDisabled={shoppingDisabled}
				{...buttonProps}
			/>
			{cart.addToCartState.error && (
				<Paragraph {...paragraphProps}>{cart.addToCartState.error}</Paragraph>
			)}
			{cart.updateCartState.error && (
				<Paragraph {...paragraphProps}>{cart.updateCartState.error}</Paragraph>
			)}
			{!isValid && (
				<Paragraph {...errorParagraphProps}>
					Valgt konfigurasjon er ikke gyldig. Vennligst se over feilmeldingene over og
					gjør nødvendige endringer.
				</Paragraph>
			)}
		</form>
	);
}
